import { useState } from "react";
import { Link } from "react-router-dom";

const contactInfo = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "service@tiantangmh.example" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 9:00 - 18:00" },
  { icon: "ri-chat-smile-3-line", label: "反馈渠道", value: "全站意见收集" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setStatus("idle");
    const form = e.currentTarget;
    const data = new FormData(form);

    // anti-spam honeypot: silently drop bot submissions
    const honey = (data.get("website_alt") as string) || "";
    if (honey.trim()) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("website_alt");

    const message = (data.get("message") as string) || "";
    if (message.trim().length > 500) {
      setFormError("留言内容不能超过 500 字，请精简后再提交。");
      setStatus("error");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("https://readdy.ai/api/form/da19dl1st185ek8asnhg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg && /spam|form data is spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="font-medium text-foreground-950">联系我们</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <span className="flex items-center justify-center gap-2 text-sm font-medium text-primary-600">
            <i className="ri-mail-send-line"></i> 联系我们
          </span>
          <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">和天堂漫画保持联系</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            有建议、有反馈、有问题？填写下方表单告诉我们，帮助我们把全库完整书库建得更好。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-5">
          {/* Info panel */}
          <aside className="rounded-3xl bg-primary-600 p-7 lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-background-50">联系信息</h2>
            <p className="mt-2 text-sm text-background-100">我们重视每一位读者的声音，通常会在收到后尽快回复。</p>
            <ul className="mt-6 space-y-5">
              {contactInfo.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background-50/15 text-xl text-background-50">
                    <i className={c.icon}></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-background-100/80">{c.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-background-50">{c.value}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl bg-background-50/10 p-4">
              <p className="text-sm leading-relaxed text-background-50">
                <i className="ri-information-line mr-1"></i>
                遇到作品资源问题？请尽量注明作品名与具体情况，便于我们快速定位处理。
              </p>
            </div>
          </aside>

          {/* Form */}
          <form
            data-readdy-form="true"
            onSubmit={handleSubmit}
            className="rounded-3xl border border-background-200 bg-background-100 p-7 lg:col-span-3"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-800">您的称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼您"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-800">联系邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="topic" className="text-sm font-medium text-foreground-800">反馈类型</label>
              <select
                id="topic"
                name="topic"
                required
                defaultValue="建议反馈"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              >
                <option>建议反馈</option>
                <option>资源问题</option>
                <option>阅读体验</option>
                <option>账号相关</option>
                <option>其他</option>
              </select>
            </div>
            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-800">留言内容（不超过 500 字）</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请在这里写下您的想法、建议或遇到的问题…"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              ></textarea>
            </div>

            {/* anti-spam honeypot */}
            <div className="alt-catch" aria-hidden="true">
              <label htmlFor="website_alt">请勿填写此栏</label>
              <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
            </div>

            {status === "success" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                <i className="ri-checkbox-circle-fill"></i>
                感谢反馈！我们已经收到您的留言，会尽快处理。
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
                <i className="ri-error-warning-fill mt-0.5"></i>
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin"></i> 提交中…</>
              ) : (
                <><i className="ri-send-plane-fill"></i> 提交反馈</>
              )}
            </button>
          </form>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系我们 - 天堂漫画",
            url: "https://hellomanga.github.io/contact",
            description: "通过联系表单或邮箱与天堂漫画团队取得联系，提交建议与反馈。",
          }),
        }}
      />
    </div>
  );
}